import React, { useState } from 'react';
import { Box, Collapse, IconButton, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { formatErrorReason, formatScheduledDateBerlin, isTechnicalError } from '../utils/format';
import type { ProcessStatus } from '../types';

export interface StatusVisual {
  label: string;
  color: string;
  showScheduledDate?: boolean;
}

export const PROCESS_STATUS_VISUALS: Record<ProcessStatus, StatusVisual> = {
  applied: { label: 'Beantragt', color: '#22C55E' },
  planned: { label: 'Geplant', color: '#60A5FA', showScheduledDate: true },
  not_sent: { label: 'Nicht versendet', color: '#EF4444' },
  cancelled: { label: 'Abgebrochen', color: '#9CA3AF' },
};

interface Props {
  processStatus: ProcessStatus | null;
  scheduledAt?: string | null;
  isManualEdit?: boolean;
  // Number of "updated" history entries — renders a "✓ geändert (N×)" pill.
  changedCount?: number;
  // Number of sends (sent + resent) — renders a "N× versendet" pill when > 1.
  sentCount?: number;
  errorReason?: string | null;
  errorDetail?: string | null;
}

const SECONDARY_PILL_SX = (color: string) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.25,
  px: 0.85,
  py: 0.25,
  borderRadius: 999,
  color,
  bgcolor: alpha(color, 0.14),
  border: `1px solid ${alpha(color, 0.35)}`,
  fontSize: 11,
  fontWeight: 700,
  lineHeight: 1.2,
  whiteSpace: 'nowrap' as const,
});

function SystemAccessLogStatusPill({
  processStatus,
  scheduledAt,
  isManualEdit,
  changedCount = 0,
  sentCount = 0,
  errorReason,
  errorDetail,
}: Props) {
  const theme = useTheme();
  const [detailExpanded, setDetailExpanded] = useState(false);
  const visual: StatusVisual = processStatus
    ? PROCESS_STATUS_VISUALS[processStatus]
    : { label: '—', color: theme.palette.text.disabled };

  // scheduled_at is anchored at 00:00 Europe/Berlin — render it in Berlin TZ so
  // the pill agrees with the detail view across browser timezones.
  const dateSuffix = visual.showScheduledDate && scheduledAt ? formatScheduledDateBerlin(scheduledAt) : '';
  // Prefer the precise history-derived count; fall back to the manual-edit flag.
  const showChanged = changedCount > 0 || Boolean(isManualEdit);
  const changedLabel = changedCount > 0 ? `geändert (${changedCount}×)` : 'geändert';

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1.25,
          py: 0.35,
          borderRadius: 999,
          color: visual.color,
          bgcolor: alpha(visual.color, 0.14),
          border: `1px solid ${alpha(visual.color, 0.35)}`,
          fontSize: 12,
          fontWeight: 700,
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
        }}
      >
        <Typography component="span" sx={{ fontSize: 12, fontWeight: 700, color: 'inherit', lineHeight: 1.2 }}>
          {visual.label}
          {dateSuffix ? ` · ${dateSuffix}` : ''}
        </Typography>
      </Box>
      {showChanged && (
        <Box sx={SECONDARY_PILL_SX('#16A34A')} aria-label="Mail wurde bearbeitet">
          <EditNoteIcon sx={{ fontSize: 14 }} />
          <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, color: 'inherit', lineHeight: 1.2 }}>
            {changedLabel}
          </Typography>
        </Box>
      )}
      {sentCount > 1 && (
        <Box sx={SECONDARY_PILL_SX('#38BDF8')} aria-label={`${sentCount} mal versendet`}>
          <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, color: 'inherit', lineHeight: 1.2 }}>
            {sentCount}× versendet
          </Typography>
        </Box>
      )}
      {processStatus === 'not_sent' && errorReason && (
        <Box sx={{ flexBasis: '100%', display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
          <Typography component="span" sx={{ fontSize: 11, color: visual.color, fontWeight: 500, lineHeight: 1.3 }}>
            {formatErrorReason(errorReason)}
          </Typography>
          {isTechnicalError(errorReason) && errorDetail && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setDetailExpanded(!detailExpanded);
              }}
              aria-label={detailExpanded ? 'Technische Details ausblenden' : 'Technische Details einblenden'}
              sx={{ p: 0.25, color: visual.color }}
            >
              {detailExpanded ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
            </IconButton>
          )}
        </Box>
      )}
      {processStatus === 'not_sent' && isTechnicalError(errorReason) && errorDetail && (
        <Box sx={{ flexBasis: '100%' }}>
          <Collapse in={detailExpanded}>
            <Box
              data-testid="error-detail-box"
              sx={{
                mt: 0.25,
                p: 0.75,
                borderRadius: 1,
                bgcolor: alpha('#EF4444', 0.07),
                border: `1px solid ${alpha('#EF4444', 0.25)}`,
                userSelect: 'text',
              }}
            >
              <Typography
                sx={{ fontSize: 11, fontFamily: 'monospace', color: '#EF4444', userSelect: 'text', lineHeight: 1.4 }}
              >
                {errorDetail}
              </Typography>
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
}

export default SystemAccessLogStatusPill;
